import React, { useState } from 'react';
import Icon from '../../../AppIcon';
import Button from '../../../ui/Button';

const CustomDashboard = () => {
  const [selectedWidgets, setSelectedWidgets] = useState([
    'revenue-trends', 'top-performers', 'conversion-funnel', 'geographic-heat'
  ]);
  
  const [isCustomizing, setIsCustomizing] = useState(false);

  const availableWidgets = [
    {
      id: 'revenue-trends',
      name: 'Revenue Trends',
      icon: 'TrendingUp',
      description: 'Track revenue growth over time',
      category: 'Financial'
    },
    {
      id: 'top-performers',
      name: 'Top Performers',
      icon: 'Award',
      description: 'Highest performing scouts and programs',
      category: 'Performance'
    },
    {
      id: 'conversion-funnel',
      name: 'Conversion Funnel',
      icon: 'Filter',
      description: 'Referral to partnership conversion rates',
      category: 'Analytics'
    },
    {
      id: 'geographic-heat',
      name: 'Geographic Heatmap',
      icon: 'Map',
      description: 'Regional activity and performance',
      category: 'Geographic'
    },
    {
      id: 'sector-analysis',
      name: 'Sector Analysis',
      icon: 'PieChart',
      description: 'Industry sector performance breakdown',
      category: 'Analytics'
    },
    {
      id: 'real-time-activity',
      name: 'Real-time Activity',
      icon: 'Activity',
      description: 'Live platform activity feed',
      category: 'Monitoring'
    },
    {
      id: 'program-health',
      name: 'Program Health',
      icon: 'Heart',
      description: 'Active program status and metrics',
      category: 'Performance'
    },
    {
      id: 'user-engagement',
      name: 'User Engagement',
      icon: 'Users',
      description: 'Platform user activity and retention',
      category: 'Analytics'
    }
  ];

  const widgetCategories = ['All', 'Financial', 'Performance', 'Analytics', 'Geographic', 'Monitoring'];
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredWidgets = selectedCategory === 'All' 
    ? availableWidgets 
    : availableWidgets?.filter(widget => widget?.category === selectedCategory);

  const toggleWidget = (widgetId) => {
    setSelectedWidgets(prev => 
      prev?.includes(widgetId)
        ? prev?.filter(id => id !== widgetId)
        : [...prev, widgetId]
    );
  };

  const renderWidget = (widgetId) => {
    const widget = availableWidgets?.find(w => w?.id === widgetId);
    if (!widget) return null;

    return (
      <div key={widgetId} className="bg-card border border-border rounded-lg p-6 relative group">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name={widget?.icon} size={18} className="text-primary" />
            </div>
            <div>
              <h4 className="font-semibold text-foreground">{widget?.name}</h4>
              <p className="text-xs text-muted-foreground">{widget?.description}</p>
            </div>
          </div>
          
          {isCustomizing && (
            <button
              onClick={() => toggleWidget(widgetId)}
              className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 text-muted-foreground hover:text-destructive"
            >
              <Icon name="X" size={16} />
            </button>
          )}
        </div>
        {/* Widget Content Placeholder */}
        <div className="h-32 bg-muted/30 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <Icon name={widget?.icon} size={32} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Widget Content</p>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
          <span>Last updated: 5 min ago</span>
          <button className="hover:text-foreground transition-colors duration-200">
            <Icon name="MoreHorizontal" size={14} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Dashboard Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">
            Custom Analytics Dashboard
          </h3>
          <p className="text-sm text-muted-foreground">
            Personalize your analytics view with customizable widgets
          </p>
        </div>

        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          <Button
            variant={isCustomizing ? "default" : "outline"}
            size="sm"
            onClick={() => setIsCustomizing(!isCustomizing)}
          >
            <Icon name="Settings" size={16} className="mr-2" />
            {isCustomizing ? 'Done' : 'Customize'}
          </Button>
          
          <Button variant="outline" size="sm">
            <Icon name="Download" size={16} className="mr-2" />
            Export
          </Button>
        </div>
      </div>
      {/* Widget Customization Panel */}
      {isCustomizing && (
        <div className="bg-muted/50 border border-border rounded-lg p-6">
          <h4 className="font-semibold text-foreground mb-4">Add Widgets to Dashboard</h4>
          
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-4">
            {widgetCategories?.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                  selectedCategory === category
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:text-foreground border border-border'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Available Widgets */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredWidgets?.map((widget) => (
              <div
                key={widget?.id}
                onClick={() => toggleWidget(widget?.id)}
                className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 ${
                  selectedWidgets?.includes(widget?.id)
                    ? 'bg-primary/10 border-primary text-primary' :'bg-background border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Icon name={widget?.icon} size={20} />
                  <div className="flex-1 min-w-0">
                    <h5 className="font-medium truncate">{widget?.name}</h5>
                    <p className="text-xs opacity-70 truncate">{widget?.description}</p>
                  </div>
                  {selectedWidgets?.includes(widget?.id) && (
                    <Icon name="Check" size={16} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Dashboard Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {selectedWidgets?.map(widgetId => renderWidget(widgetId))}
      </div>
      {/* Empty State */}
      {selectedWidgets?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="LayoutDashboard" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h4 className="text-lg font-semibold text-foreground mb-2">No Widgets Selected</h4>
          <p className="text-muted-foreground mb-4">
            Click "Customize" to add widgets to your dashboard
          </p>
          <Button onClick={() => setIsCustomizing(true)}>
            <Icon name="Plus" size={16} className="mr-2" />
            Add Widgets
          </Button>
        </div>
      )}
      {/* Dashboard Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pt-6 border-t border-border">
        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
          <div className="flex items-center space-x-2">
            <Icon name="Clock" size={16} />
            <span>Auto-refresh: 5 minutes</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Database" size={16} />
            <span>Last sync: 2 min ago</span>
          </div>
        </div>

        <div className="flex items-center space-x-2 mt-4 sm:mt-0">
          <Button variant="ghost" size="sm">
            <Icon name="RefreshCw" size={16} className="mr-2" />
            Refresh
          </Button>
          <Button variant="ghost" size="sm">
            <Icon name="Share" size={16} className="mr-2" />
            Share
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomDashboard;